import { normalizeRelayUrl, simplifyUrl, toHttpUrl } from "@/utils/url"
import type { RelayInfo } from "./types"

const CACHE_KEY = "jumble:relay-info-cache"
const MAX_ENTRIES = 300

interface CacheEntry {
  info: RelayInfo
  fetchedAt: number
}

/**
 * NIP-11 relay information document service.
 *
 * Relay metadata is fetched over HTTPS (`Accept: application/nostr+json`),
 * cached in memory and persisted to localStorage so relay cards render instantly
 * on the next visit. Every failure degrades to a minimal info object: a relay
 * without a NIP-11 document is still a usable relay.
 */
export class RelayInfoService {
  private readonly cache = new Map<string, CacheEntry>()
  private readonly inflight = new Map<string, Promise<RelayInfo>>()
  private timeoutMs = 6000
  private ttlMs = 6 * 60 * 60 * 1000
  private hydrated = false

  configure(options: { timeoutMs?: number; ttlMs?: number }): void {
    if (options.timeoutMs) this.timeoutMs = options.timeoutMs
    if (options.ttlMs) this.ttlMs = options.ttlMs
  }

  private hydrate(): void {
    if (this.hydrated || typeof localStorage === "undefined") return
    this.hydrated = true
    try {
      const raw = localStorage.getItem(CACHE_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw) as Record<string, CacheEntry>
      for (const [url, entry] of Object.entries(parsed)) {
        if (entry?.info?.url && typeof entry.fetchedAt === "number") {
          this.cache.set(url, entry)
        }
      }
    } catch {
      // a corrupt cache is not an error: it is rebuilt on demand
    }
  }

  private persist(): void {
    if (typeof localStorage === "undefined") return
    try {
      const entries = [...this.cache.entries()]
        .sort((a, b) => b[1].fetchedAt - a[1].fetchedAt)
        .slice(0, MAX_ENTRIES)
      localStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(entries)))
    } catch {
      // storage may be full or disabled; the in-memory cache still works
    }
  }

  getCached(url: string): RelayInfo | undefined {
    this.hydrate()
    const normalized = normalizeRelayUrl(url)
    if (!normalized) return undefined
    return this.cache.get(normalized)?.info
  }

  /** Returns cached info immediately, refreshing in the background when stale. */
  async get(url: string): Promise<RelayInfo> {
    this.hydrate()
    const normalized = normalizeRelayUrl(url)
    if (!normalized) {
      return { url: String(url), shortUrl: simplifyUrl(String(url)), supported_nips: [] }
    }

    const cached = this.cache.get(normalized)
    const fresh = cached && Date.now() - cached.fetchedAt < this.ttlMs
    if (cached && fresh) return cached.info

    const pending = this.inflight.get(normalized)
    if (pending) return pending

    const promise = this.fetchInfo(normalized)
      .then((info) => {
        this.cache.set(normalized, { info, fetchedAt: Date.now() })
        this.persist()
        return info
      })
      .catch(() => {
        const fallback: RelayInfo = {
          url: normalized,
          shortUrl: simplifyUrl(normalized),
          supported_nips: [],
        }
        if (cached) return cached.info
        this.cache.set(normalized, { info: fallback, fetchedAt: Date.now() })
        this.persist()
        return fallback
      })
      .finally(() => {
        this.inflight.delete(normalized)
      })

    this.inflight.set(normalized, promise)
    if (cached) {
      // Stale-while-revalidate: show the cached document, refresh silently.
      void promise
      return cached.info
    }
    return promise
  }

  async getAll(urls: string[]): Promise<RelayInfo[]> {
    return Promise.all(urls.map((url) => this.get(url)))
  }

  private async fetchInfo(url: string): Promise<RelayInfo> {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), this.timeoutMs)
    try {
      const response = await fetch(toHttpUrl(url), {
        headers: { Accept: "application/nostr+json" },
        signal: controller.signal,
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const raw = (await response.json()) as Record<string, unknown>
      return normalizeRelayInfo(url, raw)
    } finally {
      clearTimeout(timer)
    }
  }

  clear(): void {
    this.cache.clear()
    if (typeof localStorage !== "undefined") {
      try {
        localStorage.removeItem(CACHE_KEY)
      } catch {
        // ignore quota / privacy mode failures
      }
    }
  }
}

/** Validates the untrusted NIP-11 document field by field. */
export function normalizeRelayInfo(url: string, raw: Record<string, unknown>): RelayInfo {
  const text = (value: unknown, max = 2000): string | undefined =>
    typeof value === "string" && value.trim() ? value.trim().slice(0, max) : undefined
  const nips = Array.isArray(raw.supported_nips)
    ? raw.supported_nips.filter((nip): nip is number => typeof nip === "number")
    : []
  const limitation =
    raw.limitation && typeof raw.limitation === "object"
      ? (raw.limitation as Record<string, unknown>)
      : undefined
  const tags = Array.isArray(raw.tags)
    ? raw.tags.filter((tag): tag is string => typeof tag === "string").slice(0, 20)
    : undefined

  return {
    url,
    shortUrl: simplifyUrl(url),
    name: text(raw.name, 120),
    description: text(raw.description),
    icon: typeof raw.icon === "string" && /^https?:\/\//.test(raw.icon) ? raw.icon : undefined,
    pubkey: text(raw.pubkey, 64),
    contact: text(raw.contact, 300),
    supported_nips: [...new Set(nips)].sort((a, b) => a - b),
    software: text(raw.software, 300),
    version: text(raw.version, 60),
    tags,
    payments_url: text(raw.payments_url, 300),
    limitation: limitation
      ? {
          auth_required: limitation.auth_required === true,
          payment_required: limitation.payment_required === true,
          restricted_writes: limitation.restricted_writes === true,
        }
      : undefined,
  }
}

export const relayInfoService = new RelayInfoService()

export function supportsNip(info: RelayInfo | undefined, nip: number): boolean {
  return Boolean(info?.supported_nips?.includes(nip))
}

/** NIP-50 capable relays accept a `search` field in filters. */
export function supportsSearch(info: RelayInfo | undefined): boolean {
  return supportsNip(info, 50)
}

export function relayDisplayName(info: RelayInfo | undefined, url: string): string {
  return info?.name?.trim() || simplifyUrl(url)
}
